<script setup>
import { TransitionRoot, TransitionChild } from "@headlessui/vue";
import { onClickOutside } from "@vueuse/core";
import { ref, computed, watch, onMounted, onBeforeUnmount } from "vue";
import emitter from "tiny-emitter/instance";
import { useLegitimuzVerification } from "@/composables/base/useLegitimuzVerification";
import { useFocusWithin } from "@vueuse/core"; 
import { useRouter } from 'vue-router';
import { useLegitimuzStatusStore } from '@/stores/legitimuz-status';

const emit = defineEmits(["close"]);
const props = defineProps({
  show: {
    type: Boolean,
    default: false
  }
});

const router = useRouter();
const legitimuzStore = useLegitimuzStatusStore();
const target = ref(null);
const { focused } = useFocusWithin(target);
const verificationInProgress = ref(false);
const minProgressTimer = ref(null);

const {
  userStatus,
  triggerVerification,
  triggerFaceIndexVerification,
  initFaceIndex,
  isVerificationInProgress,
  legitimuzVerified
} = useLegitimuzVerification();


onMounted(() => {
  document.addEventListener('click', handleGlobalClick, true);
});

onBeforeUnmount(() => {
  document.removeEventListener('click', handleGlobalClick, true);
  // Clear any pending timers
  if (minProgressTimer.value) {
    clearTimeout(minProgressTimer.value);
  }
});


const handleGlobalClick = (event) => {
  // Check if legitimuz modal is visible
  const legitimuzModal = document.querySelector('.legitimuz-modal');
  if (!legitimuzModal && verificationInProgress.value) {
    // Legitimuz modal was closed
    console.log('Legitimuz modal closed');
    
    // Set a minimum display time for the progress state
    if (minProgressTimer.value) {
      clearTimeout(minProgressTimer.value);
    }
    
    minProgressTimer.value = setTimeout(() => {
      verificationInProgress.value = false;
      minProgressTimer.value = null;
    }, 1000); 
  }
};


const closeModal = async () => {
  // Don't allow closing if verification is in progress
  if (verificationInProgress.value) {
    return;
  }
  
  const isApproved = await legitimuzStore.checkStatus(true);
  
  if (!isApproved) {
    emit("close");
    router.push('/');
  } else {
    emit("close");
  }
};


const handleVerification = async () => {
  verificationInProgress.value = true;
  
  try {

    minProgressTimer.value = setTimeout(() => {
      minProgressTimer.value = null;

    }, 1000);
    
    if (userStatus.value === 'pending_face_id') {
      await initFaceIndex();
      triggerFaceIndexVerification();
    } else {
      triggerVerification();
    }
  } catch (error) {
    console.error("Verification initialization error:", error);
    
    if (minProgressTimer.value) {
      clearTimeout(minProgressTimer.value);
    }
    
    minProgressTimer.value = setTimeout(() => {
      verificationInProgress.value = false;
      minProgressTimer.value = null;
    }, 1000);
  }
};


watch(legitimuzVerified, (isVerified) => {
  if (isVerified) {
    if (minProgressTimer.value) {
      clearTimeout(minProgressTimer.value);
    }
    
    minProgressTimer.value = setTimeout(() => {
      verificationInProgress.value = false;
      emit("close"); 
      minProgressTimer.value = null;
    }, 500);
  }
});


const verificationText = computed(() => {
  if (userStatus.value === 'pending_face_id') {
    return "Para utilizar o sistema de apostas é necessário realizar o reconhecimento facial";
  }
  return "Para utilizar o sistema de apostas é necessário realizar a verificação dos seus documentos";
});


onClickOutside(target, () => {
  if (!verificationInProgress.value) {
    closeModal();
  }
});

const clearMinProgressTimer = () => {
  if (minProgressTimer.value) {
    clearTimeout(minProgressTimer.value);
    minProgressTimer.value = null;
  }
};

const setMinProgressTimer = (callback) => {
  clearMinProgressTimer();
  minProgressTimer.value = setTimeout(() => {
    callback();
    minProgressTimer.value = null;
  }, 500);
};

watch(
  [() => props.show, () => isVerificationInProgress.value, () => legitimuzVerified.value],
  ([showModal, sdkInProgress, verified], [prevShowModal]) => {
    if (showModal && !prevShowModal) {
      emitter.emit("set-focus", { target: target });
      verificationInProgress.value = false;
      
      clearMinProgressTimer();
    }
    
    if (!sdkInProgress && verificationInProgress.value) {
      setMinProgressTimer(() => {
        verificationInProgress.value = false;
      });
    } else if (sdkInProgress) {
      verificationInProgress.value = true;
    }
    
    if (verified) {
      setMinProgressTimer(() => {
        verificationInProgress.value = false;
        emit("close"); 
      });
    }
  },
  { deep: true }
);



</script>

<template>
  <!-- Template remains the same -->
  <TransitionRoot appear :show="show" as="div" class="scrollbar-hide fixed top-0 z-50 h-screen w-screen"
                  v-scroll-lock="true" tabindex="-1">
    <TransitionChild as="template" enter="duration-300 ease-out" enter-from="opacity-0" enter-to="opacity-100"
                     leave="duration-200 ease-in" leave-from="opacity-100" leave-to="opacity-0">
      <div class="fixed inset-0 bg-black/80" @click="closeModal()" />
    </TransitionChild>

    <div class="scrollbar-hide fixed inset-0 overflow-y-auto p-3">
      <div class="scrollbar-hide flex min-h-full items-center justify-center text-center md:p-4">
        <TransitionChild as="div" enter="duration-300 ease-out" enter-from="opacity-0 scale-95"
                         enter-to="opacity-100 scale-100" leave="duration-200 ease-in"
                         leave-from="opacity-100 scale-100"
                         leave-to="opacity-0 scale-95">
          <!-- Modal panel -->
          <div
            ref="target"
            class="scrollbar-hide relative flex h-fit w-full transform flex-col gap-4 overflow-y-auto overflow-x-hidden bg-brand-card text-left align-middle shadow-xl transition-all sm:max-w-md rounded-md">
            <div>
              <!-- Close button - disabled during verification -->
              <button @click="closeModal()"
                      :disabled="verificationInProgress"
                      :class="[
                        'text-md absolute right-3 top-3 z-50 flex items-center justify-center rounded-full transition-all p-1 bg-white/10',
                        verificationInProgress 
                          ? 'opacity-50 cursor-not-allowed text-white/30' 
                          : 'hover:bg-darken-brand-primary text-white/50 hover:text-brand-primary'
                      ]">
                <Icon name="fa6-solid:xmark" />
              </button>

              <!-- Modal title -->
              <div class="relative flex w-full flex-row items-center justify-center p-4">
                <h3 class="text-lg font-bold leading-6 text-white text-center">
                  Verificação necessária
                </h3>
              </div>
            </div>

            <!-- Modal body -->
            <div class="px-3 pb-6 pt-0 flex flex-col items-center">
              <div class="flex flex-col items-center justify-center gap-3 mb-3">
                <img src="/images/kyc.svg" alt="Legitimuz Logo" class="w-24 h-24 pb-2" />
                <p class="text-center text-white">
                  {{ verificationText }}
                </p>
                <p v-if="verificationInProgress" class="text-center text-brand-primary text-sm">
                  Verificação em andamento... Por favor, complete o processo.
                </p>
              </div>
              
              <button 
                @click="handleVerification"
                :disabled="verificationInProgress"
                :class="[
                  'w-full py-3 px-4 font-medium rounded-md transition-all',
                  verificationInProgress 
                    ? 'bg-gray-500 cursor-not-allowed' 
                    : 'bg-brand-primary hover:bg-opacity-90 text-white'
                ]">
                {{ verificationInProgress ? 'Verificação em andamento...' : 'Verificar agora' }}
              </button>
            </div>
          </div>
        </TransitionChild>
      </div>
    </div>
  </TransitionRoot>
</template>